import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Confirm,
	Modal,
	SpinnerOverlay,
} from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';

import { useMutateCheckUniqueAssetId } from '@hooks/assets/useMutateCheckUniqueAssetId';
import { useQueryAssetSubTypesByType } from '@hooks/assets/useQueryAssetSubTypesByType';
import { useQueryContactPersons } from '@hooks/assets/useQueryContactPersons';
import { useQueryResponsiblePersons } from '@hooks/assets/useQueryResponsiblePersons';

import { formattedToNumber, getFileIds } from '@utils/formatter';
import { prepareDefaultValues } from '@components/ModalComponents/AssetForm/utils';

import { TAssetProfile } from '@interfaces/assets/asset';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAssetFormValues } from '@interfaces/components/form';

import { ECoordFormat, EUserRoles } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';
import { tabsNames } from './constants';

import Address from './Address';
import Attributes from './Attributes';
import BasicInformation from './BasicInformation';
import { mainSchema } from './schema';

import styles from './AssetForm.module.scss';

interface IAssetFormProps {
	assetData?: TAssetProfile;
	isEditFromMap?: boolean;
	isEditMode?: boolean;
	isLoadingForm?: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	submitHandler?: any;
	toggleAssetModal?: () => void;
}

const AssetForm = ({
	assetData,
	isEditFromMap = false,
	isEditMode = false,
	isLoadingForm,
	setIsDirtyFormModal,
	submitHandler,
	toggleAssetModal = () => null,
}: IAssetFormProps) => {
	const [assetCheckError, setAssetCheckError] = useState<string | undefined>();
	const [selectedAssetType, setSelectedAssetType] = useState<string | null>(null);
	const [requiredFields, setRequiredFields] = useState<string[] | null>(null);

	const [initialState, setInitialState] = useState<IAssetFormValues>(() => {
		return prepareDefaultValues({ assetData, isEditMode });
	});

	const schema = useMemo(() => mainSchema({
		requiredFields,
		isEdit: isEditFromMap || isEditMode,
	}), [isEditFromMap, isEditMode, requiredFields]);

	const {
		clearErrors,
		control,
		formState: { errors, isDirty },
		getValues,
		handleSubmit,
		register,
		setValue,
		trigger,
		watch,
	} = useForm<IAssetFormValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: initialState,
	});

	const [isConfirmModal, setIsConfirmModal] = useState(false);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const handleCloseModal = () => {
		if (isDirty) setIsConfirmModal(true);
		else toggleAssetModal();
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const {
		checkUniqueAssetId,
		isLoadingCheckUniqueAssetId,
	} = useMutateCheckUniqueAssetId();

	const { assetTypesSubtypes, assetSubTypesByType } = useQueryAssetSubTypesByType(selectedAssetType);
	const { contactPersonsOptions } = useQueryContactPersons();
	const { responsibleOptions } = useQueryResponsiblePersons();

	const saveValues = (values: IAssetFormValues) => {
		return setInitialState(prevState => ({ ...prevState, ...values }));
	};

	const getDefaultSelectValueByName = (value?: string, options?: TSelectOption<string>[]) => {
		return value ? options?.find((option) => option?.name === value) || null : null;
	};

	const getDefaultSelectValueByValue = (value?: string, options?: TSelectOption<string>[]) => {
		return value ? options?.find((option) => option?.value === value) || null : null;
	};

	useEffect(() => {
		if (!isEditMode) return;

		const assetType = getDefaultSelectValueByValue(assetData?.assetType.id, assetTypesSubtypes);

		saveValues({ assetType });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, assetTypesSubtypes]);

	useEffect(() => {
		if (!isEditMode || !assetData?.assetSubType?.id) return;

		const subType = getDefaultSelectValueByValue(assetData.assetSubType.id, assetSubTypesByType);

		saveValues({ subType });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, assetSubTypesByType]);

	useEffect(() => {
		if (!isEditMode) return;

		const responsible = getDefaultSelectValueByName(assetData?.responsible?.name, responsibleOptions);

		saveValues({ responsible });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, responsibleOptions]);

	useEffect(() => {
		const defaultContactPerson = contactPersonsOptions?.find((person) =>
			person.roles.some((role: { name: string }) =>
				role.name === EUserRoles.assetOwnerDefaultContact));

		const contactPersonId = getDefaultSelectValueByName(
			assetData?.contactPerson?.id || defaultContactPerson?.id,
			contactPersonsOptions,
		);

		saveValues({ contactPersonId });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, contactPersonsOptions]);

	const submitForm = (values: IAssetFormValues) => {
		const assetType = values.assetType?.value || null;

		const lat = formattedToNumber(values.lat);
		const lng = formattedToNumber(values.lng);

		const coordinates = values.coordFormat === ECoordFormat.utm
			? { easting: lat, northing: lng }
			: {
				lat: formattedToNumber(values.lat),
				lng: formattedToNumber(values.lng),
			};

		const submitData = {
			assetAccessDetails: values?.assetAccessDetails || null,
			assetAccessDetailsFileIds: getFileIds(values.assetAccessDetailsFileIds),
			assetId: values.assetId,
			assetOwnerNotes: values.assetOwnerNotes || null,
			assetSiteAvailabilityDetails: values?.assetSiteAvailabilityDetails || null,
			assetSiteAvailabilityDetailsFileIds: getFileIds(values.assetSiteAvailabilityDetailsFileIds),
			assetSiteAvailabilityStatus: values?.assetSiteAvailabilityStatus || null,
			subType: values.subType?.value || null,
			assetTypeId: assetType,
			backhaulAccessibilityDetails: values?.backhaulAccessibilityDetails || null,
			backhaulAccessibilityDetailsFileIds: getFileIds(values.backhaulAccessibilityDetailsFileIds),
			backhaulAccessibilityStatus: values?.backhaulAccessibilityStatus || null,
			contactPersonId: values.contactPersonId?.value || null,
			height: formattedToNumber(values.height),
			indicativeAcceptablePricing: formattedToNumber(values.indicativeAcceptablePricing),
			isAvailable: values?.isAvailable,
			lastVerificationDate: values.lastVerificationDate || null,
			locationDescription: values.locationDescription || null,
			maxOperators: formattedToNumber(values.maxOperators),
			ngr: values?.ngr || null,
			powerSupplyAvailabilityDetails: values?.powerSupplyAvailabilityDetails || null,
			powerSupplyAvailabilityDetailsFileIds: getFileIds(values.powerSupplyAvailabilityDetailsFileIds),
			powerSupplyAvailabilityStatus: values?.powerSupplyAvailabilityStatus || null,
			responsible: values.responsible?.value || null,
			siteAvailabilityReviewDate: values?.siteAvailabilityReviewDate || null,
			specialImplementationIssueDetails: values?.specialImplementationIssueDetails || null,
			specialImplementationIssueDetailsFileIds: getFileIds(values.specialImplementationIssueDetailsFileIds),
			specialImplementationIssuesExist: values?.specialImplementationIssuesExist,
			uprn: formattedToNumber(values.uprn),
			...coordinates,
		};

		const submitDataWithAddress = {
			...submitData,
			adLocality: values?.adLocality || null,
			adPostCode: values?.adPostCode || null,
			adPropertyNoName: values?.adPropertyNoName || null,
			adStreetName: values?.adStreetName || null,
			adTownCity: values?.adTownCity || null,
		};

		const fields = submitDataWithAddress;

		if (isEditMode) {
			const submitCallback = () => submitHandler({
				id: assetData?.id,
				data: fields,
			});

			checkUniqueAssetId({
				assetId: fields.assetId,
				id: assetData?.id,
				successHandler: submitCallback,
				errorHandler: setAssetCheckError,
			});
		} else {
			submitHandler(fields);
		}
	};

	const tabs = [
		{
			title: 'Basic information',
			value: tabsNames.basicInformation,
			component: BasicInformation,
			isTabError: !!(assetCheckError
				|| errors?.assetId
				|| errors?.assetType
				|| errors?.subType
				|| errors?.uprn
				|| errors?.responsible
				|| errors?.indicativeAcceptablePricing
				|| errors?.maxOperators
				|| errors?.height
				|| errors?.lastVerificationDate
				|| errors?.siteAvailabilityReviewDate
				|| errors?.assetOwnerNotes
			),
			props: {
				assetCheckError,
				assetSubTypesByType,
				assetTypes: assetTypesSubtypes,
				checkUniqueAssetId,
				contactPersonsOptions,
				control,
				errors,
				getValues,
				register,
				responsibleOptions,
				setAssetCheckError,
				setSelectedAssetType,
				setRequiredFields,
				setValue,
				trigger,
				watch,
				requiredFields,
			},
		},
		{
			title: 'Address',
			value: tabsNames.address,
			component: Address,
			isTabError: !!(errors?.adPropertyNoName
				|| errors?.adStreetName
				|| errors?.adLocality
				|| errors?.adTownCity
				|| errors?.adPostCode
				|| errors?.lat
				|| errors?.lng
				|| errors?.locationDescription
			),
			props: {
				clearErrors,
				errors,
				getValues,
				register,
				setValue,
				trigger,
				watch,
				requiredFields,
			},
		},
		{
			title: 'Attributes',
			value: tabsNames.attributes,
			component: Attributes,
			props: {
				errors,
				getValues,
				handleSubmit,
				register,
				setValue,
				submitForm,
				trigger,
				watch,
				requiredFields,
			},
			isTabError: !!(errors?.assetSiteAvailabilityDetails
				|| errors?.backhaulAccessibilityDetails
				|| errors?.powerSupplyAvailabilityDetails
				|| errors?.specialImplementationIssueDetails),
		},
	];

	return (
		<div className={styles.assetForm}>
			{(isLoadingForm || isLoadingCheckUniqueAssetId) && <SpinnerOverlay />}
			<FormWithTabs
				initialState={initialState}
				isEditMode={isEditMode}
				saveValues={saveValues}
				stickyTabs
				tabs={isEditMode && !isEditFromMap ? tabs.slice(0, -1) : tabs}
			/>
			{isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={handleCloseModal}
						text='Cancel'
						withoutBackground
					/>
					<Button
						onClick={handleSubmit(submitForm)}
						text='Save'
					/>
				</div>
			}
			{isConfirmModal && (
				<Modal
					isOpen={isConfirmModal}
					onClose={handleCloseConfirmModal}
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onClose={handleCloseConfirmModal}
						onSubmit={toggleAssetModal}
						question={modalCloseConfirm.question}
					>
						{modalCloseConfirm.children}
					</Confirm>
				</Modal>
			)}
		</div>
	);
};

export default AssetForm;
